/** Expected payload from watchlist and query endpoint of Shipments API  */
export type MultiShipmentPayload = Array<{
  shipment: ShipmentPayload;
  status: number;
  trackingIds: string[];
}>;

/** Expected payload from detail endpoint of Shipments API  */
export interface ShipmentPayload {
  status: string;
  consignmentId: string;
  dynamicComms?: DynamicComms | null;
  dynamicCommsList?: Notice[] | null;
  articles: ArticlePayload[];
  merchant?: Merchant | null;
}

export interface ArticlePayload {
  articleId: string;
  status: StatusAttribute;
  atlStatus: AtlStatusAttribute | null;
  trackStatusOfArticle: string;
  statusColour: string;
  nickname: string | null;
  articleRedirect: boolean;
  userOwnership: boolean;
  atlEligible: boolean;
  redirectIneligibility: Ineligibility;
  safeDropIneligibility: Ineligibility;
  atlLocation: string | null;
  atlLocationCode: string | null;
  merchantId: string;
  barcode: string | null;
  details: ArticleDetails[];
  measurements?: Measurements | null;
  serviceCode: string | null;
  isMatchedToApcn: boolean;
  attemptedDeliveryImageExists: boolean;
}

export interface StatusAttribute {
  statusAttributeName: string;
  statusAttributeValue: string;
  statusModificationDateTime: number;
}

export interface AtlStatusAttribute {
  statusAttributeName: string;
  statusAttributeValue: string;
  statusModificationDateTime: number;
}

export interface DynamicComms {
  content: string;
  buttonText?: string;
  buttonLink?: string;
  messageId: string;
}

export interface Notice {
  title: string;
  content: string;
  linkText: string | null;
  linkUrl: string | null;
  type: string;
  url: string | null;
}

export interface Merchant {
  logoUrl: string | null;
  displayName: string;
}

export interface Ineligibility {
  code: string | null;
  reason?: string | null;
}

export const getNullAddress = () => ({
  addressee: null,
  addressLine1: null,
  addressLine2: null,
  addressLine3: null,
  suburb: null,
  state: null,
  postCode: null,
  country: null,
  countryName: null,
  addressType: null,
  deliveryPointId: null,
  articleAddressType: null,
  internationalAddress: null,
  postalAddress: null,
  customerPickupAddress: null,
  email: null,
  mobile: null,
  phone: null,
});

export interface ArticleAddress {
  addressee: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  suburb: string | null;
  state: string | null;
  postCode: string | null;
  country: string | null;
  countryName: string | null;
  addressType: AddressType | null;
  deliveryPointId: number | null;
  articleAddressType: string | null;
  internationalAddress: boolean | null;
  postalAddress: boolean | null;
  customerPickupAddress: boolean | null;
  email: string | null;
  mobile: string | null;
  phone: string | null;
  locationName?: string | null;
}

export interface EstimatedDeliveryDateRange {
  fromDate: string;
  toDate: string;
}

export interface ArticleEvent {
  dateTime: number;
  localeDateTime: string;
  description: string;
  location: string | null;
  eventCode: string;
  wcid?: string | null;
  milestone?: string | null;
  hasLocationDualName?: boolean | null;
  toWcid?: string | null;
  readyToCollectDateTime?: string | null;
}

export interface MilestoneLegacy {
  name: string;
  colour: string;
  status: string;
  progressPercentage?: number;
  dateTime?: string;
  description?: string | null;
}

export enum Milestone {
  WEVE_GOT_IT = `We've got it`,
  ON_ITS_WAY = `It's on its way`,
  COMING_TODAY = `It's coming today`,
  DELIVERED = 'Delivered',
  AWAITING_COLLECTION = 'Awaiting collection',
  PENDING = 'Pending',
  CONTACT_SENDER = 'Contact sender',
  RETURNING_TO_SENDER = 'Returning to sender',
  ATTEMPTED_DELIVERY = 'Attempted delivery',
  LABEL_CREATED_BY_SENDER = 'Label created by sender',
}

export interface CollectionInstruction {
  delegate: CollectionDelegate | null;
  facility: CollectionFacility | null;
  accessNumber: string | null;
}

export interface CollectionDelegate {
  allowed: boolean;
  reason: string | null;
}

export interface CollectionFacility {
  type?: CollectionType;
  workCentreId: string;
  deliveryPointId?: number | null;
}

export const CollectionType = {
  PARCEL_LOCKER: 'PARCEL_LOCKER',
  PARCEL_COLLECT: 'PARCEL_COLLECT',
  PO_BOX: 'PO_BOX',
  CARDED: 'CARDED',
  WORK_CENTRE: 'WORK_CENTRE',
};
export type CollectionType = (typeof CollectionType)[keyof typeof CollectionType];

export interface ArticleDetails {
  articleId: string;
  consignmentId: string;
  articleType: string;
  productSubType: string;
  sender: string | null;
  address: ArticleAddress;
  fromAddress: ArticleAddress;
  previousDelivery: ArticleAddress;
  internationalTracking: { href: string } | null;
  collectionInstruction: CollectionInstruction;
  accessCode: string | null;
  deliveredByDate: string | null;
  deliveredByDateISO: string | null;
  estimatedDeliveryDateRange: EstimatedDeliveryDateRange | null;
  estimatedDeliveryDateEligible: boolean;
  collectByDateISO: string | null;
  dateRevised: boolean;
  displaySubscriptionPrompt: boolean;
  redirectStatus: RedirectStatus | null;
  redirectAddress: ArticleAddress | null;
  extendedDescription: string | null;
  toolTipText: string | null;
  events: ArticleEvent[];
  milestones: MilestoneLegacy[] | null;
  hasNotification: boolean | null;
  measurements?: Measurements | null;
  timeWindow: TimeWindow | null;
}
export interface Measurements {
  height: Measurement | null;
  length: Measurement | null;
  width: Measurement | null;
  weight: Measurement | null;
}
export interface Measurement {
  unit?: string | null;
  value?: number | null;
}

export interface TimeWindow {
  start: string | null;
  end: string | null;
  timeZone: string | null;
  stopsAway: string | null;
  minutesAway: string | null;
  onTimeStatus: TimeWindowOnTimeStatusEnum;
  passedWindow: boolean | null;
  deliveryDate: string | null;
}

export enum TimeWindowOnTimeStatusEnum {
  EARLIER = 'Early',
  LATE = 'Late',
  PASSED = 'PASSED',
  LONG_PASSED = 'LONG_PASSED',
}

export const AtlStatusValue = {
  ELIGIBLE: 'Eligible',
  INELIGIBLE: 'Ineligible',
  REQUESTED: 'Requested',
  CANCELLED: 'Cancelled',
  PENDING: 'Pending',
};
export type AtlStatusValue = (typeof AtlStatusValue)[keyof typeof AtlStatusValue];

export const RedirectStatus = {
  REDIRECT_REQUESTED: 'Redirect Requested',
  REDIRECT_FAILED: 'Redirect Failed',
  REDIRECT_ACTIONED: 'Redirect Actioned',
  AVAILABLE: 'Available',
};
export type RedirectStatus = (typeof RedirectStatus)[keyof typeof RedirectStatus];

export const AddressType = {
  PARCEL_LOCKER: 'PL',
  PARCEL_COLLECT: 'PC',
  PO_BOX: 'PO',
  STREET: 'S',
  UNKNOWN: 'U',
};
export type AddressType = (typeof AddressType)[keyof typeof AddressType];

export enum AtlIneligibleCode {
  '1 - No signature required' = '1',
  '2 - Sender requires a signature' = '2',
  '5 - Sender requires a signature' = '5',
  '6 - Sender requires a signature' = '6',
  'X - Sender requires a signature' = 'X',
  'Y - Sender requires a signature' = 'Y',
  '7 - Sender requires a signature and ID' = '7',
}

export const ServiceCode = {
  WINE: '49',
  S8: '28',
};

export type ServiceCode = (typeof ServiceCode)[keyof typeof ServiceCode];
